import FlexibleContentTile from '../../../Shared/Tiles/FlexibleContentTile';
import RecentActivitiesNoData, { GetNoDataTile, INoDataTileMessage } from '../../../Shared/Tiles/NoDataTile';
import { useLocalTabDataHandler, APIList } from '../../../Data/useLocalTabDataHandler';
import { useEffect, useState } from 'react';
import { Table, DataTable, TableCell, TableRow, TableHeader, TableBody } from '@lmig/lmds-react-table';
import { Link } from '@lmig/lmds-react-link';
import { Pagination } from '@lmig/lmds-react-pagination';
import { getBillingPaymentServicesLink } from 'Foundation/Configurations/GlobalConfiguration';
import './Billing.scss';
import { BillingSummaryBox } from './BillingSummaryBox';
import {
  BillingSortableKeys,
  IBillingTableData,
  IBillingDocument,
  SortDirection,
  SortDirections,
} from '../../../Models/BillingModels';
import { Spinner } from '../../Spinner';
import IconInfo from '@lmig/lmds-react-icons/dist/icons/Info';
import { getRecentActivitiesLink } from 'Foundation/Configurations/GlobalConfiguration';
import { Tooltip } from '@lmig/lmds-react-tooltip';
import { StringHelper } from 'Utilities/StringHelper';

export default function Billing(props: { activeTab: number }) {
  const billingPaymentServicesLink = getBillingPaymentServicesLink();
  const [sortDirections, setSortDirections] = useState<SortDirections>({
    [BillingSortableKeys.customer]: SortDirection.none,
    [BillingSortableKeys.customerLastName]: SortDirection.none,
    [BillingSortableKeys.amountDue]: SortDirection.none,
    [BillingSortableKeys.postmarkBy]: SortDirection.ascending,
    [BillingSortableKeys.billingAccountOrPolicyNumber]: SortDirection.none,
  });
  const [currentSortKey, setCurrentSortKey] = useState<BillingSortableKeys | undefined>(BillingSortableKeys.postmarkBy);
  const [currentSortDirection, setCurrentSortDirection] = useState<SortDirection>(SortDirection.ascending);
  const [initialDataLoad, setInitialDataLoad] = useState<IBillingTableData[]>([]);
  const [billingTableData, setBillingTableData] = useState<IBillingTableData[]>([]);
  const [billingTableDataNew, setBillingTableDataNew] = useState<IBillingDocument[]>([]);
  const [billingTableDataToDisplay, setBillingTableDataToDisplay] = useState<IBillingTableData[]>([]);
  const [billingDocumentCount, setBillingDocumentCount] = useState(0);
  const [totalMinimumAmountDue, setTotalMinimumAmountDue] = useState(0);
  const recentActivitiesLink = getRecentActivitiesLink();
  const [noDataTile, setNoDataTile] = useState<INoDataTileMessage>({
    leftText: '<div>Loading...</div>',
    rightText: '<div>Loading...</div>',
  });
  const [page, setPage] = useState(0);
  const [rerender, setRerender] = useState(0);
  const { data, error, isLoading } = useLocalTabDataHandler({
    api: APIList.billing,
    body: JSON.stringify({
      sortColumn: currentSortKey,
      SortDirection: currentSortDirection,
    }),
  });
  const getCustomerFullName = (bill: IBillingDocument) => {
    if (StringHelper.hasNonEmptyString(bill.firstName) || StringHelper.hasNonEmptyString(bill.lastName)) {
      return `${bill.firstName ? bill.firstName : ''} ${bill.lastName ? bill.lastName : ''}`.trim();
    } else {
      return 'Not Available';
    }
  };

  useEffect(() => {
    const billingDataToTableData = (billing: IBillingDocument[]) => {
      return billing.map((bill: IBillingDocument) => ({
        customer: getCustomerFullName(bill),
        customerLastName: bill.lastName ? bill.lastName : '',
        amountDue: bill.minimumDueAmount,
        postmarkBy: bill.postmarkDate,
        billingAccountOrPolicyNumber: bill.billingAccountOrPolicyNumber,
      })) as IBillingTableData[];
    };

    // const isValidBilling = (bill: IBillingDataJson) =>
    //   // bill &&
    //   // bill.lastName &&
    //   // bill.firstName &&
    //   // bill.minimumDueAmount &&
    //   // bill.postmarkDate?.length > 0 &&
    //   // bill.billingAccountOrPolicyNumber &&
    //   bill.transactionType == 'BillPendingCancel';

    const handleBillingData = () =>
      new Promise((resolve, reject) => {
        if (isLoading) resolve('loading');
        if (error) reject('error fetching data');
        if (!data?.length) reject('data is empty');

        const json = data?.billingDocumentList;
        // let filteredJson = json.filter(isValidBilling);

        if (!json.length) reject('no valid bills to display');
        const mappedData = billingDataToTableData(json);
        setBillingTableDataNew(json);
        setInitialDataLoad(mappedData);

        setBillingDocumentCount(data?.totalBillingDocumentCount ?? 0);
        setTotalMinimumAmountDue(data?.sumTotalMinimumDueAmount ?? 0);

        resolve(mappedData);
      });
    handleBillingData().catch(() => {
      GetNoDataTile('billing', setNoDataTile);
    });
  }, [data, error, isLoading]);

  const sortTableData = (billingTable: Array<IBillingTableData> | undefined) => {
    let sortedBillingTableData: Array<IBillingTableData> = [];
    const isValueForKeyNotNull = (key: string) => sortDirections[key as BillingSortableKeys] !== SortDirection.none;
    const key = Object.keys(sortDirections).find(isValueForKeyNotNull) as BillingSortableKeys;
    const switchValues = (a: Array<any>) => a.slice().reverse();
    const compareValues = (valuesToCompare: Array<any>, type: string) => {
      if (type === 'number') return Number(valuesToCompare[0]) - Number(valuesToCompare[1]);
      else if (type === 'string') return valuesToCompare[0].localeCompare(valuesToCompare[1]);
      else {
        return null;
      }
    };

    if (billingTable !== undefined && billingTable?.length > 0) {
      sortedBillingTableData = billingTable.sort((a: IBillingTableData, b: IBillingTableData) => {
        let valuesToCompare = [a[key], b[key]];
        
        if (key === BillingSortableKeys.customerLastName) {
          const aValue = !a[key] ? a[BillingSortableKeys.customer] : a[key];
          const bValue = !b[key] ? b[BillingSortableKeys.customer] : b[key];
          
          valuesToCompare = [aValue, bValue];
        }

        if (sortDirections[key] === SortDirection.descending) valuesToCompare = switchValues(valuesToCompare);
        if (key === BillingSortableKeys.postmarkBy) valuesToCompare = switchValues(valuesToCompare);
        if (key === BillingSortableKeys.amountDue) return compareValues(valuesToCompare, 'number');
        return compareValues(valuesToCompare, 'string');
      });
    }
    return sortedBillingTableData;
  };

  useEffect(() => {
    let sortedTableData = sortTableData(initialDataLoad);
    setBillingTableData(sortedTableData);
    setPage(0);
    setRerender((r) => r + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirections, initialDataLoad]);

  useEffect(() => {
    setBillingTableDataToDisplay(billingTableData.slice(0, 5));
  }, [billingTableData]);

  const billingTableSorter = (key: BillingSortableKeys) => {
    const getNone = {
      customer: SortDirection.none,
      customerLastName: SortDirection.none,
      amountDue: SortDirection.none,
      postmarkBy: SortDirection.none,
      billingAccountOrPolicyNumber: SortDirection.none,
    };
    setSortDirections((prev) => ({
      ...getNone,
      [key]: prev[key] === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending,
    }));
    setCurrentSortKey(key);
    setCurrentSortDirection(sortDirections[key]);
  };

  useEffect(() => {
    const start = page * 5;
    setBillingTableDataToDisplay(billingTableData.slice(start, Math.min(start + 5, billingTableData.length)));
    setRerender((r) => r + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const LMDSPagination = () => {
    const changeHandler = (e: any) => {
      setPage(e.page);
    };
    const itemCount = billingTableData.length;

    return (
      (itemCount > 5 && (
        <Pagination
          id="billing-pagination"
          className="recent-activities-pagination"
          withWords={false}
          itemCount={itemCount}
          onChange={changeHandler}
          showItemsPerPage={false}
          page={page}
          itemsPerPage={5}
        />
      )) || <></>
    );
  };

  const formatRenewalChangeAmount = (amount: number) => {
    const numericalAmount = Number(amount);
    return `${numericalAmount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    })}`;
  };

  const formatDateString = (date: string) => {
    const dateFields = date.split('-');
    if (dateFields.length >= 3) {
      return dateFields[1] + '/' + dateFields[2] + '/' + dateFields[0];
    } else {
      return '';
    }
  };

  return isLoading ? (
    <Spinner />
  ) : initialDataLoad?.length &&
    billingTableData?.length > 0 &&
    billingTableDataNew?.length > 0 &&
    billingDocumentCount &&
    totalMinimumAmountDue ? (
    <FlexibleContentTile
      data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
      tileClass="billing-tile"
      children={[
        <div id="billing-table-container" className="recent-activities-table-container">
          <div key={props.activeTab} className="billing-tab-content tab-content">
            <BillingSummaryBox
              totalBillingDocumentCount={billingDocumentCount}
              sumTotalMinimumDueAmount={totalMinimumAmountDue}
              billingPaymentServicesLink={billingPaymentServicesLink}
            ></BillingSummaryBox>
            <div className="design-system-data-table-container">
              <div className="design-system-data-table">
                <DataTable>
                  <Table className="design-system-table" spacing="micro">
                    <TableHeader id="billing-table-header" className="recent-activities-table-header">
                      <TableRow>
                        <TableCell
                          type="colHead"
                          isSortable
                          sortDirection={sortDirections.customerLastName}
                          onClick={() => billingTableSorter(BillingSortableKeys.customerLastName)}
                        >
                          Customer
                        </TableCell>
                        <TableCell
                          type="colHead"
                          isSortable
                          sortDirection={sortDirections.amountDue}
                          onClick={() => billingTableSorter(BillingSortableKeys.amountDue)}
                          className="billing-table-cell small-column-header"
                        >
                          <div className="billing-amountdue-header">Amount due</div>
                        </TableCell>
                        <TableCell
                          type="colHead"
                          isSortable
                          sortDirection={sortDirections.postmarkBy}
                          onClick={() => billingTableSorter(BillingSortableKeys.postmarkBy)}
                          className="small-column-header"
                        >
                          Postmark by
                        </TableCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody key={rerender}>
                      {billingTableDataToDisplay.map((i: IBillingTableData, rowIndex: any) => (
                        <TableRow key={`${rowIndex}-billing`}>
                          <TableCell key={`${rowIndex}-billing-customer-name`}>
                            <div className="customer-name-container billing-customer-name">
                              <Tooltip
                                className="recent-activities-tile-tooltip"
                                content={
                                  <Link
                                    target="_blank"
                                    href={
                                      billingPaymentServicesLink + '?iscbp=Y&account=' + i.billingAccountOrPolicyNumber
                                    }
                                  >
                                    {i.customer}
                                  </Link>
                                }
                              >
                                <div className="customer-name">
                                  <Link
                                    target="_blank"
                                    href={
                                      billingPaymentServicesLink + '?iscbp=Y&account=' + i.billingAccountOrPolicyNumber
                                    }
                                  >
                                    {i.customer}
                                  </Link>
                                </div>
                              </Tooltip>
                            </div>
                          </TableCell>
                          <TableCell key={`${rowIndex}-billing-amount-due`}>
                            <div className="billing-amount-due-column">{formatRenewalChangeAmount(i.amountDue)}</div>
                          </TableCell>
                          <TableCell key={`${rowIndex}-billing-billing-date`}>
                            <div className="billing-date-column">{formatDateString(i.postmarkBy)}</div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </DataTable>
              </div>
              {billingDocumentCount > 50 && page === 9 ? (
                <div className="recent-activities-info-view-all">
                  <div className="recent-activities-info">
                    <IconInfo size="16" color="informative" />
                    Viewing only 50 most recent activities.
                    {recentActivitiesLink ? (
                      <Link
                        target="_blank"
                        href={recentActivitiesLink}
                        variant="standalone"
                        className="portal-tile-v2-header-link"
                      >
                        View all activity
                      </Link>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
          <LMDSPagination />
        </div>,
      ]}
    ></FlexibleContentTile>
  ) : (
    <RecentActivitiesNoData
      messageLeft={noDataTile.leftText}
      messageRight={noDataTile.rightText}
    ></RecentActivitiesNoData>
  );
}
